import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  PurchaseOrderItem,
  CostCenterStrategy,
  MockProject,
  MockWorkCategory,
  mockUnits
} from './PurchaseOrderFormData';
import { Plus, Trash2, Package } from 'lucide-react';
import { ItemAllocationsEditor } from './ItemAllocationsEditor';

interface PurchaseItemsTableProps {
  items: PurchaseOrderItem[];
  strategy: CostCenterStrategy;
  currency: string;
  onChange: (items: PurchaseOrderItem[]) => void;
  projects: MockProject[];
  generalWorkCategories: MockWorkCategory[];
}

export const PurchaseItemsTable: React.FC<PurchaseItemsTableProps> = ({
  items,
  strategy,
  currency,
  onChange,
  projects,
  generalWorkCategories
}) => {

  const addNewItem = () => {
    const newItem: PurchaseOrderItem = {
      id: `item-${Date.now()}`,
      description: '',
      unit: 'UN',
      unitPrice: 0,
      quantity: strategy === 'GLOBAL' || strategy === 'UNASSIGNED' ? 1 : undefined,
      allocations: [],
    };
    onChange([...items, newItem]);
  };

  const removeItem = (id: string) => {
    onChange(items.filter(item => item.id !== id));
  };

  const updateItem = (id: string, updates: Partial<PurchaseOrderItem>) => {
    onChange(items.map(item => item.id === id ? { ...item, ...updates } : item));
  };

  const getTotalQuantity = (item: PurchaseOrderItem): number => {
    if (strategy === 'GLOBAL' || strategy === 'UNASSIGNED') {
      return item.quantity || 0;
    }
    return item.allocations.reduce((sum, alloc) => sum + alloc.allocatedQuantity, 0);
  };

  const getTotalAmount = (item: PurchaseOrderItem): number => {
    return getTotalQuantity(item) * item.unitPrice;
  };

  const grandTotal = items.reduce((sum, item) => sum + getTotalAmount(item), 0);

  return (
    <div className="overflow-hidden border rounded-lg shadow-sm bg-card border-border">
      <div className="px-4 py-5 border-b sm:px-6 border-border">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="flex items-center gap-2 text-lg font-semibold text-foreground">
              <Package className="w-5 h-5 text-primary" />
              Items de Compra
            </h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Define los productos o servicios a comprar
            </p>
          </div>
          <Button
            onClick={addNewItem}
            className="flex items-center gap-2 rounded-lg bg-primary text-primary-foreground hover:opacity-90"
          >
            <Plus className="w-4 h-4" />
            Agregar Item
          </Button>
        </div>
      </div>

      <div className="px-4 py-6 sm:px-6 space-y-4">
        {items.length === 0 ? (
          <div className="py-12 text-center border-2 border-dashed rounded-lg border-border">
            <Package className="w-12 h-12 mx-auto mb-3 text-muted-foreground/40" />
            <p className="mb-2 text-sm font-medium text-muted-foreground">No hay items agregados</p>
            <p className="mb-4 text-xs text-muted-foreground/70">Comienza agregando un item a la orden de compra</p>
            <Button
              onClick={addNewItem}
              variant="outline"
              className="rounded-lg"
            >
              <Plus className="w-4 h-4 mr-2" />
              Agregar Primer Item
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {items.map((item, index) => (
              <div key={item.id} className="p-4 border rounded-lg bg-muted/30 border-border">
                {/* Item Header */}
                <div className="flex items-center gap-2 mb-3">
                  <span className="flex items-center justify-center w-6 h-6 text-xs font-bold rounded-full bg-primary text-primary-foreground">
                    {index + 1}
                  </span>
                  <span className="text-sm font-semibold text-foreground">Item {index + 1}</span>
                </div>

                {/* Item Fields */}
                <div className="grid grid-cols-1 gap-3 mb-3 md:grid-cols-3 lg:grid-cols-6">
                  {/* Descripción */}
                  <div className="space-y-1 md:col-span-2">
                    <Label className="text-xs font-medium text-foreground">
                      Descripción <span className="text-xs text-red-500">*</span>
                    </Label>
                    <Input
                      value={item.description}
                      onChange={(e) => updateItem(item.id, { description: e.target.value })}
                      placeholder="Ej: Cemento Portland..."
                      className="text-sm rounded-lg bg-background border-border focus-visible:ring-primary"
                    />
                  </div>

                  {/* Unidad */}
                  <div className="space-y-1">
                    <Label className="text-xs font-medium text-foreground">
                      Unidad <span className="text-xs text-red-500">*</span>
                    </Label>
                    <Select
                      value={item.unit}
                      onValueChange={(value) => updateItem(item.id, { unit: value })}
                    >
                      <SelectTrigger className="text-sm rounded-lg bg-background border-border">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {mockUnits.map((unit) => (
                          <SelectItem key={unit} value={unit}>{unit}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Precio Unitario */}
                  <div className="space-y-1">
                    <Label className="text-xs font-medium text-foreground">
                      Precio Unit. <span className="text-xs text-red-500">*</span>
                    </Label>
                    <Input
                      type="number"
                      value={item.unitPrice || ''}
                      onChange={(e) => updateItem(item.id, { unitPrice: Number(e.target.value) || 0 })}
                      placeholder="0.00"
                      className="text-sm rounded-lg bg-background border-border focus-visible:ring-primary"
                      min="0"
                      step="0.01"
                    />
                  </div>

                  {/* Cantidad (solo GLOBAL y UNASSIGNED) */}
                  {(strategy === 'GLOBAL' || strategy === 'UNASSIGNED') && (
                    <div className="space-y-1">
                      <Label className="text-xs font-medium text-foreground">
                        Cantidad <span className="text-xs text-red-500">*</span>
                      </Label>
                      <Input
                        type="number"
                        value={item.quantity || ''}
                        onChange={(e) => updateItem(item.id, { quantity: Number(e.target.value) || 0 })}
                        placeholder="0.00"
                        className="text-sm rounded-lg bg-background border-border focus-visible:ring-primary"
                        min="0"
                        step="0.01"
                      />
                    </div>
                  )}

                  {/* Cantidad Total (solo PER_LINE - readonly) */}
                  {strategy === 'PER_LINE' && (
                    <div className="space-y-1">
                      <Label className="text-xs font-medium text-foreground">Cantidad Total</Label>
                      <Input
                        type="text"
                        value={getTotalQuantity(item).toFixed(2)}
                        readOnly
                        className="text-sm rounded-lg bg-muted border-border"
                      />
                    </div>
                  )}

                  {/* Importe Total */}
                  <div className="space-y-1">
                    <Label className="text-xs font-medium text-foreground">Importe Total</Label>
                    <Input
                      type="text"
                      value={`${currency} ${getTotalAmount(item).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                      readOnly
                      className="text-sm font-semibold rounded-lg bg-primary/5 border-primary/20 text-primary"
                    />
                  </div>

                  {/* Botón Eliminar */}
                  <div className="flex items-end space-y-1">
                    <Button
                      onClick={() => removeItem(item.id)}
                      variant="outline"
                      size="sm"
                      className="w-full rounded-lg border-destructive/30 text-destructive hover:bg-destructive/10"
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Eliminar
                    </Button>
                  </div>
                </div>

                {/* Allocations Editor (PER_LINE only) */}
                {strategy === 'PER_LINE' && (
                  <div className="mt-3">
                    <ItemAllocationsEditor
                      allocations={item.allocations}
                      unitPrice={item.unitPrice}
                      unit={item.unit}
                      currency={currency}
                      onChange={(allocations: any) => updateItem(item.id, { allocations })}
                      projects={projects}
                      generalWorkCategories={generalWorkCategories}
                    />
                  </div>
                )}
              </div>
            ))}

            {/* Grand Total */}
            <div className="flex items-center justify-between p-4 border-2 rounded-lg border-primary/20 bg-primary/5">
              <span className="text-lg font-bold text-foreground">TOTAL GENERAL</span>
              <span className="text-2xl font-bold text-primary">
                {currency} {grandTotal.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
